import { useEffect } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { FileText, ArrowRight } from "lucide-react";
import { LEGAL_DOCS, LEGAL_ALIASES } from "@/content/legal";
import { BRAND } from "@/lib/brand";

export function LegalIndex() {
  return (
    <div className="container-x py-16" data-testid="legal-index-page">
      <p className="label-eyebrow">Legal</p>
      <h1 className="mt-2 text-4xl font-bold tracking-tighter sm:text-5xl">Policies & agreements</h1>
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {LEGAL_DOCS.map((d) => (
          <Link key={d.slug} to={`/legal/${d.slug}`} data-testid={`legal-index-${d.slug}`} className="group rounded-3xl border border-black/5 bg-white p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg">
            <FileText className="h-6 w-6 text-[#FF5A00]" />
            <h2 className="mt-5 text-lg font-semibold">{d.title}</h2>
            <p className="mt-1 text-sm text-neutral-600">{d.summary}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#FF5A00]">Read <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" /></span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function LegalPage() {
  const { slug } = useParams();
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  }, [hash, slug]);
  if (LEGAL_ALIASES[slug]) return <Navigate to={`/legal/${LEGAL_ALIASES[slug]}#${slug}`} replace />;
  const doc = LEGAL_DOCS.find((d) => d.slug === slug);
  if (!doc) return <div className="container-x py-24" data-testid="legal-not-found">Page not found. <Link className="text-[#FF5A00]" to="/legal">All policies</Link></div>;
  return (
    <div className="container-x grid gap-12 py-16 lg:grid-cols-[240px_1fr]" data-testid={`legal-page-${slug}`}>
      <aside className="hidden lg:block">
        <nav className="sticky top-24 space-y-1">
          {LEGAL_DOCS.map((d) => (
            <Link key={d.slug} to={`/legal/${d.slug}`} className={`block rounded-xl px-3 py-2 text-sm transition-colors duration-200 ${d.slug === slug ? "bg-neutral-950 text-white" : "text-neutral-600 hover:bg-white"}`}>{d.title}</Link>
          ))}
        </nav>
      </aside>
      <article className="prose-legal max-w-3xl">
        <p className="label-eyebrow">Legal</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tighter sm:text-5xl" data-testid="legal-title">{doc.title}</h1>
        <p className="!mt-4 !text-sm !text-neutral-500" data-testid="legal-meta">Effective date: {BRAND.effectiveDate} · Version {BRAND.legalVersion} · {BRAND.legalName}</p>
        <nav className="!mt-8 rounded-2xl border border-black/5 bg-white p-5" data-testid="legal-toc">
          <p className="!m-0 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">On this page</p>
          <ol className="!mb-0 !mt-3 !list-none columns-1 gap-6 !pl-0 text-sm sm:columns-2">
            {doc.sections.map((s, i) => (
              <li key={s.h} className="!my-1 break-inside-avoid"><a href={`#${s.id || `s${i}`}`} onClick={(e) => { e.preventDefault(); document.getElementById(s.id || `s${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); }} className="text-neutral-700 no-underline transition-colors duration-200 hover:text-[#FF5A00]">{s.h}</a></li>
            ))}
          </ol>
        </nav>
        {doc.sections.map((s, i) => (
          <section key={s.h} id={s.id || `s${i}`} className="scroll-mt-24">
            <h2>{s.h}</h2>
            {(s.p || []).map((t) => <p key={t}>{t}</p>)}
            {s.ul && <ul>{s.ul.map((t) => <li key={t}>{t}</li>)}</ul>}
          </section>
        ))}
      </article>
    </div>
  );
}
